/**
 * LeadAssignModal — pick an assignee for a lead ("ללא שיוך" allowed).
 * Also reused as the "pick a treating therapist before converting" step.
 */
import { useEffect, useState } from "react";
import { UserCircle, Check } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { therapistDisplayName } from "./leadsUtils";

function LeadAssignModal({
  lead,
  therapists = [],
  currentTherapistId,
  allowNone = true,
  title = "שיוך למטפל/ת",
  hint,
  confirmLabel = "שמור שיוך",
  isSaving = false,
  onClose,
  onConfirm, // (therapistId|null, therapist|null) => Promise
}) {
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (lead) setSelected(lead.therapistId || "");
  }, [lead]);

  if (!lead) return null;

  const pick = (id) => setSelected(id);
  const canConfirm = !isSaving && (allowNone || !!selected);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ld-modal ld-assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="tb-modal-title">
            <span className="tb-modal-icon">
              <UserCircle size={18} weight="fill" />
            </span>
            <div className="ld-modal-heading">
              <span className="ld-eyebrow">{lead.name}</span>
              <h3>{title}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div className="modal-body">
          {hint && <p className="ld-muted-text">{hint}</p>}
          <div className="ld-assign-list" role="listbox">
            {allowNone && (
              <button type="button" role="option" aria-selected={selected === ""} className={`ld-assign-row${selected === "" ? " is-selected" : ""}`} onClick={() => pick("")} disabled={isSaving}>
                <span className="ld-assign-avatar is-pool">—</span>
                <span className="ld-assign-name">ללא שיוך</span>
                {selected === "" && <Check size={16} weight="bold" />}
              </button>
            )}
            {therapists.map((t) => {
              const name = therapistDisplayName(t);
              const isMe = t.uid === currentTherapistId;
              return (
                <button key={t.uid} type="button" role="option" aria-selected={selected === t.uid} className={`ld-assign-row${selected === t.uid ? " is-selected" : ""}`} onClick={() => pick(t.uid)} disabled={isSaving}>
                  <span className="ld-assign-avatar">{(name || "?").charAt(0)}</span>
                  <span className="ld-assign-name">
                    {name}
                    {isMe && <span className="ld-chip ld-chip-neutral">אני</span>}
                  </span>
                  {selected === t.uid && <Check size={16} weight="bold" />}
                </button>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isSaving}>
            ביטול
          </button>
          <button
            className="btn-primary"
            disabled={!canConfirm}
            onClick={() => onConfirm(selected || null, therapists.find((t) => t.uid === selected) || null)}
          >
            {isSaving && <LoaderLoop size="xs" />}
            <span>{confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default LeadAssignModal;
